import { z } from 'zod';
import { CLIENT_AD_EVENT_DESTINATIONS } from './destinations.js';
import { ADDRESS_FIELD, ADDRESS_SUBFIELD, DELIVERY_OPTION, PREQUAL_OUTCOME, ECOMMERCE_PAYLOAD } from './shared-fields.js';

/**
 * Express-checkout events: the funnel, step, field, and engagement signals for
 * the standalone Express prequal/finance flow (`apps/checkout/src/pages/express`).
 *
 * `ex_` is a naming surface, not an app, exactly as `po_` is. Express runs inside
 * the checkout app, declares `surface: 'checkout'`, and carries the checkout base
 * props; the prefix separates the funnel because Express and core checkout are
 * reported separately and collect different fields. Express reuses no `co_`
 * entry: a borrowed event forces Express to satisfy core's schema with values it
 * does not have, which is how `cartId: ''` and a hardcoded `sessionType` reached
 * the warehouse and made the two surfaces indistinguishable in the fields that
 * were supposed to tell them apart.
 *
 * Express is pre-cart. A cart is minted at the handoff into core and nowhere
 * else, so `cartId` appears on the two terminal events alone; every other entry
 * uses `inventoryItemId` (a base prop) for vehicle context. Cross-surface
 * identity needs nothing added: `ekho_aid` and `ekho_sid` ride every event and
 * survive the same-origin handoff.
 *
 * No entry here carries a `wireName`. Every Express event emits under its own
 * `ex_` canonical, which is a clean break rather than a migration.
 *
 * A `wireName` buys continuity with rows an event already produced, and neither
 * kind of history here is worth keeping. The five borrowed events emitted under
 * core's names, so those rows are core's and preserving the name would leave
 * Express indistinguishable from checkout forever, which is the defect this
 * surface exists to remove. The rest emitted under `co_*` names that collided
 * with core's in the console and in every breakdown, which is the confusion the
 * prefix was introduced to end. Keeping either would mean shipping the new
 * vocabulary while the wire kept speaking the old one.
 *
 * The cost is a rename downstream: reports keyed on the old strings stop seeing
 * Express rows and have to move to the `ex_` names. That is the intended
 * outcome, and it happens once.
 *
 * One place vocabulary. `EXPRESS_STEP` names the five stages of the flow, and
 * every entry that names a place draws on it: where the buyer moved to, where a
 * step failed, where they abandoned, where they resumed, and which step a field
 * belongs to. Answering "where did they leave from" and "where did they move to"
 * in the same terms is what makes the two joinable without translation.
 */
const SURFACE_CHECKOUT = 'checkout';
// ─── Shared vocabularies ──────────────────────────────────────────────────────
/**
 * The five stages of the Express flow, in order.
 *
 * `payment` is a member even though it emits path events of its own: the buyer
 * sees it as a stage in the sidebar and presses a gate button to leave it, so a
 * step enum that omitted it would report a four-step funnel over a five-step UI.
 *
 * The cash path purges `prequal`, `credit_app` and `fulfillment` from the flow
 * rather than skipping them, so a cash buyer completes two stages of two. Read
 * completion against the path, never against the member count.
 */
const EXPRESS_STEP = z
    .enum([
    'your_details',
    'payment',
    'prequal',
    'credit_app',
    'fulfillment',
    'card_details',
    'review',
])
    .describe('Which stage of the Express flow.');
/**
 * The credit-application sections Express collects.
 *
 * Deliberately not `CREDIT_APP_SECTION`. Express splits housing into its own
 * section where core folds it into another, so a shared enum would either drop a
 * section Express genuinely has or force a member into
 * `EKHO_FINANCING_APPLICATION_SECTIONS`, which drives core's heading anchors and
 * scroll priority rather than reporting alone. The two enums describe two forms
 * that happen to share a purpose.
 */
const EXPRESS_CREDIT_APP_SUBSECTION = z
    .enum(['terms', 'housing', 'employment', 'additional_income', 'applicant'])
    .describe('Which subsection of the Express credit application.');
/**
 * Where a field event happened, at whichever grain the surrounding form has.
 * `step` is always present; `section` narrows to a credit-application section and
 * is absent everywhere else, because no other Express step has sections.
 */
const FIELD_LOCATION = {
    step: EXPRESS_STEP,
    section: EXPRESS_CREDIT_APP_SUBSECTION.optional().describe('Which credit-application section the field sits in. Absent outside the credit-app step, which has no sections.'),
};
/** Control types for field-level events. */
const CONTROL_TYPE = z
    .enum(['text', 'select', 'checkbox', 'radio', 'date'])
    .describe('What kind of input the buyer was using.');
/**
 * Why a field failed validation.
 *
 * A bounded code rather than the rendered message. A rendered message is
 * unqueryable (reword the copy and every historical row stops matching) and is a
 * standing PII risk, because the moment a validator interpolates what the buyer
 * typed it reaches the CDP with nothing in the way.
 */
const FIELD_ERROR_REASON = z
    .enum([
    'required',
    'invalid',
    'too_short',
    'too_long',
    'out_of_range',
    'unsupported_value',
    'underage',
])
    .describe('Why the input was rejected.');
/** Why a step did not advance. */
const STEP_FAILURE_REASON = z
    .enum([
    'validation',
    'registration_incomplete',
    'handoff_failed',
    'provider_error',
    // A background estimator was mid-recompute, so the step refused rather than
    // commit the buyer to a fee the panel had already superseded. Transient and
    // self-clearing, so its rate is what says whether the gate is costing
    // presses rather than catching them.
    'prices_recalculating',
    // Express tried to place the order itself and could not. Each reason names
    // a different missing precondition, so the rate of each says which part of
    // the session is not ready rather than that placement failed.
    'order_buyer_unresolved',
    'order_providers_unresolved',
    'order_totals_unresolved',
    'order_registration_invalid',
    'order_tax_estimate_unresolved',
    'order_credit_app_unprotected',
    'order_pickup_fees_unset',
    'order_placement_rejected',
    // Order creation refused a promotion the buyer applied, so the buyer is
    // sent back to the payment step with it removed.
    'order_promotions_rejected',
    // A pay-in-full order was placed but its card charge did not go through.
    // Declined means the card itself was refused, so the buyer has to use a
    // different one; failed is anything a retry can get past.
    'payment_declined',
    'payment_failed',
])
    .describe('What stopped the step from advancing.');
/**
 * Which part of a step was outstanding when it failed.
 *
 * Section-scoped on purpose: the step event names the sections that blocked, and
 * `ex_field_errored` names the individual inputs. Two grains, two events, and
 * neither derivable from the other.
 */
const STEP_BLOCKER = z
    .enum([
    'basic_info',
    'address',
    'terms',
    'housing',
    'employment',
    'additional_income',
    'applicant',
    'shipping_address',
    'registration_address',
    'disclosures',
    'atv_acknowledgments',
])
    .describe('Which section of the step was outstanding.');
/** The payment path a buyer can take through Express. */
const PAYMENT_PATH = z
    .enum(['financing', 'cash'])
    .describe('Which way the buyer is paying for the vehicle.');
/** How a technical call failed, where the buyer could do nothing about it. */
const ERROR_TYPE = z
    .enum(['network', 'timeout', 'provider_error', 'invalid_request', 'unknown'])
    .describe('How the call failed.');
// ─── Reused property groups ───────────────────────────────────────────────────
/**
 * The vehicle the flow is about, resolved from the listing at entry.
 *
 * `inventoryItemId` is a base prop and stays out of here. These are the fields a
 * reader needs to segment without joining to inventory: what it is, what it
 * costs, and whether it trips the ATV gate.
 */
const VEHICLE_CONTEXT = {
    vehicleProductId: z.string().describe('Catalog product id of the vehicle being bought.'),
    vehicleType: z
        .string()
        .describe('Vehicle category from the canon vehicle. `ATV` is the value the federal age gate keys on, which is what makes the gate derivable rather than needing an event of its own.'),
    vehiclePrice: z.number().describe('Vehicle retail price in USD, before accessories and fees.'),
};
/**
 * The join into core, carried by the two events that mint a cart and by nothing
 * else. Express has no cart before this point, so an entry that declared one
 * would be promising an id that cannot exist.
 */
const HANDOFF_KEYS = {
    cartId: z.string().describe('The CheckoutCart minted at the handoff, joining Express to core.'),
    totalCartCost: z.number().describe('Cart total in USD at the moment the cart was created.'),
};
const EXPRESS_EVENTS = {
    // ───────────────────────────────────────────────────────────────────────────
    // Flow lifecycle
    // ───────────────────────────────────────────────────────────────────────────
    EXPRESS_FLOW_STARTED: {
        canonical: 'ex_flow_started',
        surface: SURFACE_CHECKOUT,
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'InitiateCheckout',
            tiktok: 'InitiateCheckout',
            tracklution: 'checkout_started',
            gtm: 'begin_checkout',
        },
        description: 'Express finished loading and is ready to use: the first block has painted and the buyer can act. Anchored to paint rather than to the seed fetchers resolving, because data arriving is not the same as a usable screen and the two invert on a slow connection. Once per visit, and mutually exclusive with `ex_flow_resumed`.',
        schema: z.object({
            ...VEHICLE_CONTEXT,
            accessoriesTotal: z
                .number()
                .describe('Sum of accessories bundled with the listing, in USD. Zero when none.'),
            defaultPaymentPath: PAYMENT_PATH.describe('The path preselected before the buyer chose anything. Carried here so "the buyer kept the default" stays derivable from the absence of `ex_payment_path_selected`.'),
            defaultDeliveryOption: DELIVERY_OPTION.describe('The fulfilment option preselected before the buyer chose anything.'),
            eligiblePromotionCount: z
                .number()
                .int()
                .describe('How many promotions the listing resolved as eligible at entry.'),
            eligiblePromotionSavings: z
                .number()
                .describe('Best available promotion saving in USD at entry. Zero when none apply.'),
            trigger: z
                .enum(['first_block_painted', 'fallback_engine_ready'])
                .describe('Which path fired the event. `fallback_engine_ready` means the paint signal never arrived and the engine-ready moment was used instead.'),
        }),
        ecommerceSchema: ECOMMERCE_PAYLOAD,
    },
    EXPRESS_FLOW_RESUMED: {
        canonical: 'ex_flow_resumed',
        surface: SURFACE_CHECKOUT,
        description: 'A returning buyer picked up a saved Express flow with at least one step already complete. Once per visit, and mutually exclusive with `ex_flow_started`.',
        schema: z.object({
            ...VEHICLE_CONTEXT,
            resumedAt: EXPRESS_STEP.describe('The step the saved flow reopened on.'),
            stepsCompleted: z
                .number()
                .int()
                .describe('How many steps were already complete when the flow reopened.'),
        }),
    },
    EXPRESS_FLOW_COMPLETED: {
        canonical: 'ex_flow_completed',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer finished everything Express asks of them and is being handed to core. Fires on both paths, so it is the single measure of a successful Express visit; `path` separates the two, and `stepsCompleted` differs because the cash path purges the last three stages rather than skipping them. Carries the whole deal so the outcome reads in one row without replaying the selection events. Once per visit.',
        schema: z.object({
            ...HANDOFF_KEYS,
            ...VEHICLE_CONTEXT,
            path: PAYMENT_PATH.describe('Which way the buyer chose to pay.'),
            stepsCompleted: z
                .number()
                .int()
                .describe('How many stages the buyer completed. Two on the cash path, five on financing.'),
            elapsedMs: z
                .number()
                .int()
                .describe('Milliseconds from the flow becoming usable to the handoff.'),
            deliveryOption: DELIVERY_OPTION.optional().describe('The fulfilment option the buyer settled on. Absent on the cash path, which hands off before the fulfilment stage exists.'),
            termMonths: z
                .number()
                .int()
                .optional()
                .describe('Loan length the buyer requested. Absent on the cash path.'),
            downPayment: z
                .number()
                .optional()
                .describe('Down payment the buyer requested, in USD. Absent on the cash path.'),
            prequalOutcome: PREQUAL_OUTCOME.optional().describe('What the soft pull decided. Absent on the cash path, and absent on financing when the pull errored rather than returning a decision.'),
            eligiblePromotionSavings: z
                .number()
                .describe('Best available promotion saving in USD at handoff. Zero when none apply.'),
        }),
    },
    EXPRESS_FLOW_ABANDONED: {
        canonical: 'ex_flow_abandoned',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer left Express with work in progress. Suppressed on the handoff navigation, so a successful crossing is never counted as an abandonment. Intentionally un-deduped: the handler runs on every unload, including a same-tab reload, so the count is arrivals lost rather than people lost.',
        schema: z.object({
            ...VEHICLE_CONTEXT,
            abandonedAt: EXPRESS_STEP.describe('The step the buyer was on when they left.'),
            stepsCompleted: z.number().int().describe('How many steps they had finished before leaving.'),
        }),
    },
    // ───────────────────────────────────────────────────────────────────────────
    // Movement and step outcomes
    // ───────────────────────────────────────────────────────────────────────────
    EXPRESS_STEP_MOVED: {
        canonical: 'ex_step_moved',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer arrived at a stage of the flow. Reports where they landed, so the sequence of these is the route they took and direction is the relationship between consecutive arrivals rather than a property. The only event that sees a backward move, and the only one that sees anything at all on a resumed session, where the completion events are correctly suppressed for stages already finished. Un-deduped, capped at 15 firings per visit.',
        schema: z.object({
            step: EXPRESS_STEP.describe('The stage the buyer arrived at.'),
            fromStep: EXPRESS_STEP.optional().describe('The stage they came from. Absent on the first arrival of a visit, which has no predecessor.'),
        }),
    },
    EXPRESS_STEP_FAILED: {
        canonical: 'ex_step_failed',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer tried to leave a stage and could not. Covers everything that holds them there: client validation, an address the registration gate will not accept, a lender that errored instead of deciding, and a cart that failed to mint. `reasons` separates a problem the buyer can fix from one they cannot. Un-deduped, capped at 5 firings per step per visit.',
        schema: z.object({
            step: EXPRESS_STEP.describe('The stage that did not advance.'),
            reasons: z.array(STEP_FAILURE_REASON).describe('Why it did not advance.'),
            validationErrors: z
                .array(STEP_BLOCKER)
                .describe('Which sections were outstanding. Empty when the cause was not the buyer.'),
            errorType: ERROR_TYPE.optional().describe('How the call failed. Present only for `handoff_failed` and `provider_error`.'),
            httpStatus: z
                .number()
                .int()
                .optional()
                .describe('Status the failing call returned. Absent when the failure produced no response.'),
        }),
    },
    // ───────────────────────────────────────────────────────────────────────────
    // Stage completions
    //
    // One entry per stage rather than one event with a `step` discriminator. A
    // registry entry holds exactly one `adEventNames` map, and three of these
    // stages map to different external funnel steps, so they cannot share one.
    // The two that carry no map are split anyway: a half-split leaves a reader
    // unable to tell which stages have their own event, and a name is the most
    // expensive string in the system to change later.
    // ───────────────────────────────────────────────────────────────────────────
    EXPRESS_DETAILS_COMPLETED: {
        canonical: 'ex_details_completed',
        surface: SURFACE_CHECKOUT,
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'Contact',
            tiktok: 'Contact',
            tracklution: 'checkout_contact_info_submitted',
            gtm: 'contact_info_submitted',
        },
        description: 'The buyer finished the details stage: name, email, phone and home address are all valid and they advanced. Buyer identity rides `opts.contactInfo` for ad-platform lead matching and never as event properties. Once per visit.',
        schema: z.object({}),
    },
    EXPRESS_PAYMENT_PATH_COMPLETED: {
        canonical: 'ex_payment_path_completed',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer confirmed how they are paying and left the payment stage. The flow continues on both paths, and no cart exists yet at this point, so `cartId` and `totalCartCost` are not sent. Once per visit.',
        schema: z.object({
            path: PAYMENT_PATH.describe('The path the buyer committed to.'),
            cartId: HANDOFF_KEYS.cartId
                .optional()
                .describe('Not sent: the cart is created at the fulfillment step, after this event.'),
            totalCartCost: HANDOFF_KEYS.totalCartCost
                .optional()
                .describe('Not sent: no cart exists yet when this event fires.'),
        }),
    },
    EXPRESS_PREQUAL_COMPLETED: {
        canonical: 'ex_prequal_completed',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer read their pre-qualification result and carried on into the credit application. `ex_prequal_result_received` minus this event is the population that saw a decision and left. Once per visit.',
        schema: z.object({
            outcome: PREQUAL_OUTCOME.describe('The decision they carried on from.'),
        }),
    },
    EXPRESS_CREDIT_APP_COMPLETED: {
        canonical: 'ex_credit_app_completed',
        surface: SURFACE_CHECKOUT,
        description: 'Every section of the credit application is valid and the buyer advanced. Once per visit.',
        schema: z.object({
            sectionsCompleted: z
                .number()
                .int()
                .describe('How many sections were saved. Five when the whole form was filled in order.'),
        }),
    },
    EXPRESS_FULFILLMENT_COMPLETED: {
        canonical: 'ex_fulfillment_completed',
        surface: SURFACE_CHECKOUT,
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'AddPaymentInfo',
            tiktok: 'AddPaymentInfo',
            tracklution: 'payment_info_submitted',
            gtm: 'add_payment_info',
        },
        description: 'The financing terminal: the credit application was submitted, the cart was minted, and the buyer is being handed to core. Carries the ad-platform payment step for the financing workflow, which is the only place it can fire: a financed deal is placed at $0 due today and enters payment details nowhere, so without this the external payment step is missing for every financed buyer. Once per visit.',
        schema: z.object({
            ...HANDOFF_KEYS,
            deliveryOption: DELIVERY_OPTION.describe('The fulfilment option the buyer settled on.'),
            vehicleProductIds: z.array(z.string()).describe('Catalog product ids in the minted cart.'),
        }),
        ecommerceSchema: ECOMMERCE_PAYLOAD,
    },
    // ───────────────────────────────────────────────────────────────────────────
    // Prequal
    // ───────────────────────────────────────────────────────────────────────────
    EXPRESS_PREQUAL_SUBMITTED: {
        canonical: 'ex_prequal_submitted',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer pressed the pre-qualification button, which is the FCRA consent act and the trigger for the soft pull in one press. The gate press for the prequal stage, which is why the stage has a submit event where the other four do not: press and outcome are seconds apart across a lender call, and the population between them is real. The authoritative consent record is persisted with the prequal request server-side; this is the behavioural copy. Un-deduped, capped at 3 firings per visit.',
        schema: z.object({
            disclosureVersion: z
                .string()
                .describe('Version identifier of the FCRA consent text the buyer was shown.'),
        }),
    },
    EXPRESS_PREQUAL_RESULT_RECEIVED: {
        canonical: 'ex_prequal_result_received',
        surface: SURFACE_CHECKOUT,
        description: 'The credit engine returned a decision. A call that errored instead of deciding reports `ex_step_failed` with `provider_error`, so this event means a verdict exists. Once per submission.',
        schema: z.object({
            outcome: PREQUAL_OUTCOME,
            rejectionReasons: z
                .array(z.string())
                .describe('Why the engine declined, as it reported them. Empty on an approval. Carried because "how many buyers were denied" is answerable without it and "why" is not.'),
            prequalId: z
                .string()
                .describe('The lender file number for this decision, passed as a property rather than read from the session: the session is written after this event fires, so the base prop is still empty here.'),
        }),
    },
    // ───────────────────────────────────────────────────────────────────────────
    // Credit application
    // ───────────────────────────────────────────────────────────────────────────
    EXPRESS_CREDIT_APP_SUBSECTION_COMPLETED: {
        canonical: 'ex_credit_app_subsection_completed',
        surface: SURFACE_CHECKOUT,
        description: 'A section of the credit application became valid and saved. Covers all five sections, including the terms and applicant sections that can block the step. Once per section per visit; re-saving after an edit does not re-fire, but the values carried are the ones that saved most recently. Empty is valid for the additional-income section, so its completion means the buyer resolved it rather than filled it in.',
        schema: z.object({
            section: EXPRESS_CREDIT_APP_SUBSECTION,
            wasEmpty: z
                .boolean()
                .describe('Whether the section saved with nothing entered. Only the additional-income section can be valid while empty, and it is the difference between a buyer who has no other income and one who never engaged.'),
            termMonths: z
                .number()
                .int()
                .optional()
                .describe('Loan length requested. Present on the terms section only.'),
            downPayment: z
                .number()
                .optional()
                .describe('Down payment requested, in USD. Present on the terms section only.'),
        }),
    },
    // ───────────────────────────────────────────────────────────────────────────
    // Selections
    // ───────────────────────────────────────────────────────────────────────────
    EXPRESS_PAYMENT_PATH_SELECTED: {
        canonical: 'ex_payment_path_selected',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer acted on the payment cards. Fires on a genuine change and on a deliberate re-press of the card already active, because a preselected default means silence otherwise cannot distinguish a buyer who considered the choice from one who never saw it. The path they left the stage with rides `ex_payment_path_completed`, so this event carries consideration rather than commitment. Un-deduped, capped at 5 firings per visit.',
        schema: z.object({
            path: PAYMENT_PATH.describe('The path the buyer pressed.'),
            previousPath: PAYMENT_PATH.describe('The path in effect before the press.'),
            changed: z
                .boolean()
                .describe('Whether the press moved the buyer. False when they re-pressed the active card.'),
        }),
    },
    EXPRESS_DELIVERY_OPTION_SELECTED: {
        canonical: 'ex_delivery_option_selected',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer changed how they want the vehicle delivered. Never fires for the option preselected from the listing, which rides `ex_flow_started.defaultDeliveryOption`. Un-deduped, capped at 5 firings per visit.',
        schema: z.object({
            deliveryOption: DELIVERY_OPTION.describe('The option the buyer moved to.'),
            previousOption: DELIVERY_OPTION.describe('The option in effect before the change.'),
        }),
    },
    EXPRESS_DISCLOSURES_TOGGLED: {
        canonical: 'ex_disclosures_toggled',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer ticked or unticked the required disclosures. Every flip is reported, including unticking and re-ticking: for a consent control the hesitation is the signal, and there is no keystroke behind a checkbox so the volume cannot run away. The authoritative consent state rides `ex_fulfillment_completed`, which is the event that matters legally.',
        schema: z.object({
            agreed: z.boolean().describe('The state the box arrived at.'),
        }),
    },
    EXPRESS_ATV_AGE_GATE_CONFIRMED: {
        canonical: 'ex_atv_age_gate_confirmed',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer satisfied the federal ATV age check: both acknowledgments ticked and a rider birth date that passes. The population that saw the gate is derivable from `ex_flow_started.vehicleType` and arrival at the fulfilment stage, so only the buyer act needs reporting. Once per visit.',
        schema: z.object({}),
    },
    // ───────────────────────────────────────────────────────────────────────────
    // Form fields
    // ───────────────────────────────────────────────────────────────────────────
    EXPRESS_FIELD_STARTED: {
        canonical: 'ex_field_started',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer began filling a typed input. Typed input only: a choice from a bounded set is valid by construction and has no start-then-finish shape, so radios, selects and checkboxes report through their own domain events instead. Once per field per visit.',
        schema: z.object({
            ...FIELD_LOCATION,
            fieldName: z.string().describe('Which input, by name only. Never its value.'),
            controlType: CONTROL_TYPE,
        }),
    },
    EXPRESS_FIELD_COMPLETED: {
        canonical: 'ex_field_completed',
        surface: SURFACE_CHECKOUT,
        description: 'A typed input reached a valid value and the buyer moved off it. Once per field per visit.',
        schema: z.object({
            ...FIELD_LOCATION,
            fieldName: z.string().describe('Which input, by name only. Never its value.'),
            controlType: CONTROL_TYPE,
        }),
    },
    EXPRESS_FIELD_ERRORED: {
        canonical: 'ex_field_errored',
        surface: SURFACE_CHECKOUT,
        description: 'An input was rejected by the validator driving the UI, so the event and the screen always agree. Once per field per reason per visit, re-arming once the field goes valid again, so a buyer who fixes a field and breaks it the same way reports twice.',
        schema: z.object({
            ...FIELD_LOCATION,
            fieldName: z.string().describe('Which input, by name only. Never its value.'),
            controlType: CONTROL_TYPE,
            reasons: z.array(FIELD_ERROR_REASON).describe('Why it was rejected.'),
        }),
    },
    // ───────────────────────────────────────────────────────────────────────────
    // Address
    //
    // Express collects an address up to three times and the registration address
    // gates the terminal step, so the form has real ways to go wrong and earns
    // field-level treatment. Mirrors checkout's address entries, which exist
    // because a validator disagreement was findable only once the data did.
    // ───────────────────────────────────────────────────────────────────────────
    EXPRESS_ADDRESS_SUGGESTION_SELECTED: {
        canonical: 'ex_address_suggestion_selected',
        surface: SURFACE_CHECKOUT,
        description: 'Buyer picked an address from the autocomplete dropdown. Once per distinct address per field.',
        schema: z.object({
            addressField: ADDRESS_FIELD,
            suggestionRank: z.number().int().describe('Which row they picked, counting from zero.'),
            suggestionCount: z.number().int().describe('How many rows were on offer.'),
            queryLength: z.number().int().describe('How many characters they had typed.'),
        }),
    },
    EXPRESS_ADDRESS_MANUAL_ENTRY_SELECTED: {
        canonical: 'ex_address_manual_entry_selected',
        surface: SURFACE_CHECKOUT,
        description: 'Buyer chose to type their address by hand rather than use autocomplete. Which route they took predicts whether the address will need normalising, and the field events cannot tell the two apart. Once per address field per visit.',
        schema: z.object({
            addressField: ADDRESS_FIELD,
            source: z.enum(['link', 'dropdown']).describe('Which control took them to manual entry.'),
            queryLength: z.number().int().describe('How many characters they had typed first.'),
            suggestionCount: z.number().int().describe('How many suggestions they abandoned.'),
        }),
    },
    EXPRESS_ADDRESS_FIELD_EDITED: {
        canonical: 'ex_address_field_edited',
        surface: SURFACE_CHECKOUT,
        description: 'Buyer changed one of the manual address inputs and moved focus away. Reports every edit rather than the first, because repeated edits on one line are the friction signal. Un-deduped, capped at 5 firings per input per visit.',
        schema: z.object({
            addressField: ADDRESS_FIELD,
            field: ADDRESS_SUBFIELD,
            becameEmpty: z.boolean().describe('Whether the edit left the input empty.'),
            trigger: z
                .enum(['manual', 'autofill'])
                .describe('Whether the buyer typed it or the browser filled it.'),
            addressOrigin: z
                .enum(['autocomplete', 'manual'])
                .describe('How the address being edited was arrived at in the first place.'),
        }),
    },
    EXPRESS_ADDRESS_CLEARED: {
        canonical: 'ex_address_cleared',
        surface: SURFACE_CHECKOUT,
        description: 'Buyer emptied the address field, leaving none held. Reported so the stream stops claiming an address the buyer no longer has. Un-deduped, capped at 5 firings per visit.',
        schema: z.object({
            addressField: ADDRESS_FIELD,
            wasComplete: z.boolean().describe('Whether the address they cleared had been valid.'),
        }),
    },
    EXPRESS_ADDRESS_POPUP_SUGGESTION_SHOWN: {
        canonical: 'ex_address_popup_suggestion_shown',
        surface: SURFACE_CHECKOUT,
        description: 'The address-confirmation popup opened, because the lookup returned something materially different from what the buyer entered. `_shown` rather than `_opened`: the system revealed it without the buyer asking. Once per distinct address per field.',
        schema: z.object({
            addressField: ADDRESS_FIELD,
            reason: z
                .enum(['normalization', 'missing_zip4', 'missing_county'])
                .describe('Why the suggestion was offered.'),
        }),
    },
    EXPRESS_ADDRESS_POPUP_SUGGESTION_CONFIRMED: {
        canonical: 'ex_address_popup_suggestion_confirmed',
        surface: SURFACE_CHECKOUT,
        description: 'Buyer resolved the address-confirmation popup and an address was kept. Once per distinct address per field.',
        schema: z.object({
            addressField: ADDRESS_FIELD,
            choice: z
                .enum(['suggested', 'entered', 'edited'])
                .describe('Which address the buyer ended up with.'),
        }),
    },
    EXPRESS_ADDRESS_POPUP_SUGGESTION_DECLINED: {
        canonical: 'ex_address_popup_suggestion_declined',
        surface: SURFACE_CHECKOUT,
        description: 'Buyer dismissed the address-confirmation popup without accepting the normalised address. Distinct from confirming with `entered`: a refusal says the suggestion was understood and rejected, a dismissal says it was in the way. Once per distinct address per field.',
        schema: z.object({ addressField: ADDRESS_FIELD }),
    },
    // ───────────────────────────────────────────────────────────────────────────
    // Engagement
    // ───────────────────────────────────────────────────────────────────────────
    EXPRESS_CHAT_BUTTON_CLICKED: {
        canonical: 'ex_chat_button_clicked',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer opened support from the Express top bar. Carries the stage they were on, which is the whole value of the event: where buyers reach for help is actionable, that someone clicked chat is not. Un-deduped, capped at 5 firings per visit.',
        schema: z.object({
            step: EXPRESS_STEP.describe('The stage the buyer was on when they asked for help.'),
        }),
    },
    EXPRESS_LEGAL_LINK_CLICKED: {
        canonical: 'ex_legal_link_clicked',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer opened one of the legal or informational documents linked from Express. Each opens in a new tab, so the click is the whole of what happens here. Un-deduped, capped at 5 firings per visit.',
        schema: z.object({
            link: z
                .enum(['terms', 'privacy', 'powered_by_ekho'])
                .describe('Which document the buyer opened.'),
            step: EXPRESS_STEP.describe('The stage they opened it from.'),
        }),
    },
    EXPRESS_FINANCING_CALCULATOR_OPENED: {
        canonical: 'ex_financing_calculator_opened',
        surface: SURFACE_CHECKOUT,
        description: 'The buyer revealed the monthly-payment breakdown, on whichever surface their viewport offers. `_opened` rather than `_shown`: the panel is visible by default on desktop, so an event here always follows a deliberate press. Once per visit per surface.',
        schema: z.object({
            surfaceControl: z
                .enum(['summary_panel', 'bottom_sheet'])
                .describe('Which presentation of the estimate the buyer opened.'),
            estimatedMonthly: z.number().describe('Estimated monthly payment shown, in USD.'),
            termMonths: z.number().int().describe('Loan length the estimate assumed.'),
            apr: z.number().describe('APR the estimate assumed, as a percentage.'),
            downPayment: z.number().describe('Down payment the estimate assumed, in USD.'),
        }),
    },
};

export { EXPRESS_EVENTS, EXPRESS_STEP };
//# sourceMappingURL=express.js.map
